import { FC } from "react";

import { Card } from "react-bootstrap";

import { Users, Search, Building2, Mail, Phone, Loader2, CheckCircle } from "lucide-react";

import { Customer } from "@/_services/invoiceService";

interface Props {
  customerSearchQuery: string;
  setCustomerSearchQuery: (query: string) => void;
  loadingCustomers: boolean;
  filteredCustomers: Customer[];
  selectedCustomer: string;
  customers: Customer[];
  selectedCustomerData?: Customer | null;
  onCustomerSelection: (customerId: string) => void;
  isCustomerAutoSelected?: boolean;
  autoSelectionSource?: 'quote' | 'diagnostic' | null;
}

const CustomerSelectionCard: FC<Props> = ({
  customerSearchQuery,
  setCustomerSearchQuery,
  loadingCustomers,
  filteredCustomers,
  selectedCustomer,
  customers,
  selectedCustomerData,
  onCustomerSelection,
  isCustomerAutoSelected = false,
  autoSelectionSource = null,
}) => {
  return (
    <Card className="border-0 shadow-sm bg-white">
      <Card.Header>
        <Card.Title className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-purple-600" />
            <span className="text-sm">Sélection du client</span>
          </div>
          {isCustomerAutoSelected && (
            <div className="flex items-center space-x-1 text-green-600">
              <CheckCircle className="w-3 h-3" />
              <span className="text-xs">Auto-sélectionné</span>
            </div>
          )}
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={customerSearchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCustomerSearchQuery(e.target.value)
              }
              className="w-full pl-10 pr-4 py-2 border text-sm border-gray-300 rounded-lg focus:border-transparent"
            />
          </div>

          {customerSearchQuery && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {loadingCustomers ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-gray-600">
                    Recherche de clients...
                  </span>
                </div>
              ) : filteredCustomers?.length > 0 ? (
                filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedCustomer === customer.id.toString()
                        ? "border-purple-300 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => onCustomerSelection(customer.id.toString())}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">
                          {customer.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {customer.customer_type === "company"
                            ? "Entreprise"
                            : "Particulier"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {customer.emails.length > 0
                            ? customer.emails[0]
                            : "Pas d'email"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {customer.phone || "Pas de téléphone"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center p-4 text-gray-500 text-sm">
                  Aucun client trouvé
                </div>
              )}
            </div>
          )}

          {selectedCustomer && (
            <div className={`mt-4 p-4 rounded-lg ${
              isCustomerAutoSelected ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
            }`}>
              {isCustomerAutoSelected && (
                <div className="flex items-center space-x-2 mb-3 text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {autoSelectionSource === 'diagnostic' 
                      ? "Client sélectionné automatiquement depuis les données du diagnostic"
                      : "Client sélectionné automatiquement depuis le devis"
                    }
                  </span>
                </div>
              )}
              {(() => {
                const customer =
                  selectedCustomerData ||
                  customers.find((c) => c.id.toString() === selectedCustomer) ||
                  filteredCustomers.find(
                    (c) => c.id.toString() === selectedCustomer
                  );

                return customer ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{customer.name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {customer.emails.length > 0
                          ? customer.emails[0]
                          : "Aucun email"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {customer.phone || "Aucun téléphone"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {customer.billing_address.address && (
                        <>
                          {customer.billing_address.address}
                          <br />
                          {customer.billing_address.postal_code}{" "}
                          {customer.billing_address.city}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-red-500 text-sm">
                    Customer not found for ID: {selectedCustomer}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default CustomerSelectionCard;
